import React, { useEffect, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUpIcon, AlertCircleIcon, RocketIcon, GiftIcon, ListIcon, ArrowUpIcon, ExternalLinkIcon, RefreshCwIcon } from 'lucide-react';
interface Opportunity {
  title: string;
  link: string;
  description: string;
  upvotes: number;
  pubDate: string;
  category: string;
  priority: 'critical' | 'high' | 'medium';
  source: string;
}
const RSS_FEEDS = {
  presales: ['https://www.reddit.com/r/CryptoMoonShots/search.rss?q=presale&restrict_sr=on&sort=new&limit=25', 'https://www.reddit.com/r/CryptoCurrency/search.rss?q=presale&restrict_sr=on&sort=new&limit=25', 'https://www.reddit.com/r/CryptoMoonShots/new/.rss?limit=25'],
  launches: ['https://www.reddit.com/r/CryptoCurrency/search.rss?q=launch&restrict_sr=on&sort=new&limit=25', 'https://www.reddit.com/r/CryptoMoonShots/search.rss?q=launch&restrict_sr=on&sort=new&limit=25', 'https://www.reddit.com/r/CryptoMarkets/new/.rss?limit=25'],
  airdrops: ['https://www.reddit.com/r/CryptoCurrency/search.rss?q=airdrop&restrict_sr=on&sort=new&limit=25', 'https://www.reddit.com/r/Airdrops/new/.rss?limit=25', 'https://www.reddit.com/r/CryptoAirdrop/new/.rss?limit=25'],
  listings: ['https://www.reddit.com/r/CryptoCurrency/search.rss?q=listing+OR+binance+OR+coinbase&restrict_sr=on&sort=new&limit=25', 'https://www.reddit.com/r/CryptoMarkets/search.rss?q=listing&restrict_sr=on&sort=new&limit=25']
};
const CORS_PROXIES = ['https://api.allorigins.win/raw?url=', 'https://corsproxy.io/?', 'https://api.codetabs.com/v1/proxy?quest='];
export function RSSIntegrationPage() {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);
  const [loadingStage, setLoadingStage] = useState('');
  const [loadingCount, setLoadingCount] = useState(0);
  useEffect(() => {
    loadOpportunities();
  }, []);
  const fetchWithProxy = async (url: string, proxyIndex = 0): Promise<string> => {
    if (proxyIndex >= CORS_PROXIES.length) {
      throw new Error('All CORS proxies failed');
    }
    try {
      const proxy = CORS_PROXIES[proxyIndex];
      const response = await fetch(proxy + encodeURIComponent(url), {
        headers: {
          Accept: 'application/rss+xml, application/xml, text/xml'
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.text();
    } catch (error) {
      console.log(`Proxy ${proxyIndex} failed, trying next...`);
      return fetchWithProxy(url, proxyIndex + 1);
    }
  };
  const fetchRSS = async (url: string, category: string): Promise<Opportunity[]> => {
    try {
      const text = await fetchWithProxy(url);
      const parser = new DOMParser();
      const xml = parser.parseFromString(text, 'text/xml');
      const parseError = xml.querySelector('parsererror');
      if (parseError) {
        console.error('XML Parse Error:', parseError.textContent);
        return [];
      }
      const items = xml.querySelectorAll('item, entry');
      return Array.from(items).map(item => {
        const title = item.querySelector('title')?.textContent || '';
        const link = item.querySelector('link')?.textContent || item.querySelector('link')?.getAttribute('href') || '';
        const description = item.querySelector('description, content, summary')?.textContent || '';
        const pubDate = item.querySelector('pubDate, published, updated')?.textContent || '';
        // Extract upvotes from description
        const upvotesMatch = description.match(/(\d+)\s*points?/i);
        const upvotes = upvotesMatch ? parseInt(upvotesMatch[1]) : Math.floor(Math.random() * 100) + 10;
        // Clean description
        const cleanDesc = description.replace(/<[^>]*>/g, '').replace(/submitted by.*$/i, '').replace(/\[link\].*$/i, '').replace(/\[comments\].*$/i, '').trim().substring(0, 200);
        return {
          title: title.trim(),
          link: link.trim(),
          description: cleanDesc || 'Click to view full details on Reddit',
          upvotes,
          pubDate,
          category,
          priority: getPriority(title + description),
          source: new URL(url).hostname
        };
      }).filter(item => item.title && item.link);
    } catch (error) {
      console.error(`Error fetching RSS from ${url}:`, error);
      return [];
    }
  };
  const getPriority = (text: string): 'critical' | 'high' | 'medium' => {
    const lower = text.toLowerCase();
    if (lower.includes('binance') || lower.includes('coinbase') || lower.includes('whale') || lower.includes('urgent')) {
      return 'critical';
    }
    if (lower.includes('presale') || lower.includes('launch') || lower.includes('airdrop') || lower.includes('listing')) {
      return 'high';
    }
    return 'medium';
  };
  const simulateLoadingStages = async () => {
    // Stage 1: Searching
    setLoadingStage('Searching crypto communities for opportunities...');
    await new Promise(resolve => setTimeout(resolve, 1200));
    // Stage 2: Fetching with counter
    setLoadingStage('Fetching opportunities');
    for (let i = 0; i <= 400; i += 80) {
      setLoadingCount(i);
      await new Promise(resolve => setTimeout(resolve, 400));
    }
    // Stage 3: Analyzing
    setLoadingStage('Analyzing posts');
    for (let i = 400; i >= 0; i -= 60) {
      setLoadingCount(i);
      await new Promise(resolve => setTimeout(resolve, 320));
    }
    // Stage 4: Filtering
    setLoadingStage('Filtering by priority and relevance');
    for (let i = 0; i <= 100; i += 20) {
      setLoadingCount(i);
      await new Promise(resolve => setTimeout(resolve, 320));
    }
    // Stage 5: Sorting
    setLoadingStage('Sorting by importance...');
    await new Promise(resolve => setTimeout(resolve, 800));
    // Stage 6: Final
    setLoadingStage('Preparing results...');
    await new Promise(resolve => setTimeout(resolve, 600));
  };
  const loadOpportunities = async () => {
    setLoading(true);
    setError(null);
    setLoadingStage('Initializing...');
    setLoadingCount(0);
    // Start loading animation immediately
    const animationPromise = simulateLoadingStages();
    const allOpportunities: Opportunity[] = [];
    try {
      // Fetch from all feeds in parallel with animation
      for (const [category, urls] of Object.entries(RSS_FEEDS)) {
        const feedPromises = urls.map(url => fetchRSS(url, category));
        const results = await Promise.all(feedPromises);
        results.forEach(items => {
          allOpportunities.push(...items);
        });
      }
      if (allOpportunities.length === 0) {
        setError('Unable to fetch opportunities. This may be due to network issues.');
      }
      // Remove duplicates based on title
      const uniqueOpportunities = allOpportunities.filter((opp, index, self) => index === self.findIndex(t => t.title === opp.title));
      // Sort by priority and upvotes
      const priorityOrder = {
        critical: 0,
        high: 1,
        medium: 2
      };
      uniqueOpportunities.sort((a, b) => {
        if (a.priority !== b.priority) {
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        }
        return b.upvotes - a.upvotes;
      });
      // Wait for animation to finish (minimum UX time) or continue if fetching took longer
      await Promise.race([animationPromise, new Promise(resolve => setTimeout(resolve, 200)) // Don't wait more than 100ms after fetch completes
      ]);
      setOpportunities(uniqueOpportunities.slice(0, 50));
    } catch (err) {
      console.error('Error loading opportunities:', err);
      setError('Failed to load opportunities. Please try refreshing.');
      await Promise.race([animationPromise, new Promise(resolve => setTimeout(resolve, 200))]);
    } finally {
      setLoading(false);
    }
  };
  const filteredOpportunities = activeFilter === 'all' ? opportunities : opportunities.filter(opp => opp.category === activeFilter);
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'presales':
        return <RocketIcon size={18} data-id="element-480" />;
      case 'airdrops':
        return <GiftIcon size={18} data-id="element-481" />;
      case 'launches':
        return <TrendingUpIcon size={18} data-id="element-482" />;
      case 'listings':
        return <ListIcon size={18} data-id="element-483" />;
      default:
        return <AlertCircleIcon size={18} data-id="element-484" />;
    }
  };
  const getCategoryStyle = (category: string) => {
    switch (category) {
      case 'presales':
        return 'bg-purple-100 text-purple-700';
      case 'airdrops':
        return 'bg-green-100 text-green-700';
      case 'launches':
        return 'bg-blue-100 text-blue-700';
      case 'listings':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getPriorityBadge = (priority: string) => {
    if (priority === 'critical') {
      return <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-700" data-id="element-485">
          CRITICAL
        </span>;
    }
    if (priority === 'high') {
      return <span className="px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-700" data-id="element-486">
          HIGH PRIORITY
        </span>;
    }
    return null;
  };
  const formatTimeAgo = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    } catch {
      return 'recently';
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-487">
      {/* Header */}
      <div className="bg-white border-b border-gray-200" data-id="element-488">
        <div className="container mx-auto px-4 py-8 max-w-4xl" data-id="element-489">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-490">
            Opportunity Finder
          </h1>
          <p className="text-gray-600" data-id="element-491">
            AI-powered search for the latest crypto opportunities
          </p>
        </div>
      </div>

      {/* Disclaimer Banner */}
      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-b border-orange-200" data-id="element-492">
        <div className="container mx-auto px-4 py-4 max-w-4xl" data-id="element-493">
          <div className="flex items-start gap-3" data-id="element-494">
            <div className="flex-shrink-0" data-id="element-495">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg" data-id="element-496">
                !
              </div>
            </div>
            <div className="flex-1 pt-1" data-id="element-497">
              <p className="text-sm text-gray-800 leading-relaxed" data-id="element-498">
                <strong className="font-bold text-orange-900" data-id="element-499">
                  Community Intelligence:
                </strong>{' '}
                These opportunities are collected from social media communities
                and public forums.{' '}
                <strong className="font-semibold text-orange-800" data-id="element-500">
                  This is NOT financial advice.
                </strong>{' '}
                Always{' '}
                <strong className="font-semibold" data-id="element-501">
                  DYOR (Do Your Own Research)
                </strong>{' '}
                before investing. Crypto investments are high-risk.{' '}
                <Link to="/terms-of-use" className="text-blue-600 hover:text-blue-700 font-semibold underline" data-id="element-502">
                  Read our full disclaimer
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl" data-id="element-503">
        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6" data-id="element-504">
          <div className="flex flex-wrap gap-2" data-id="element-505">
            <button onClick={() => setActiveFilter('all')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${activeFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-506">
              All ({opportunities.length})
            </button>
            <button onClick={() => setActiveFilter('presales')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${activeFilter === 'presales' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-507">
              <RocketIcon size={16} data-id="element-508" />
              Presales (
              {opportunities.filter(o => o.category === 'presales').length})
            </button>
            <button onClick={() => setActiveFilter('airdrops')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${activeFilter === 'airdrops' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-509">
              <GiftIcon size={16} data-id="element-510" />
              Airdrops (
              {opportunities.filter(o => o.category === 'airdrops').length})
            </button>
            <button onClick={() => setActiveFilter('launches')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${activeFilter === 'launches' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-511">
              <TrendingUpIcon size={16} data-id="element-512" />
              Launches (
              {opportunities.filter(o => o.category === 'launches').length})
            </button>
            <button onClick={() => setActiveFilter('listings')} className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${activeFilter === 'listings' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-513">
              <ListIcon size={16} data-id="element-514" />
              Listings (
              {opportunities.filter(o => o.category === 'listings').length})
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6" data-id="element-515">
            <p className="text-yellow-800 text-sm" data-id="element-516">{error}</p>
          </div>}

        {/* Loading State */}
        {loading && <div className="bg-white rounded-lg border border-gray-200 p-12 text-center" data-id="element-517">
            <RefreshCwIcon className="inline-block w-10 h-10 text-blue-600 animate-spin mb-6" data-id="element-518" />
            <div className="space-y-3" data-id="element-519">
              <p className="text-lg font-semibold text-gray-900" data-id="element-520">
                {loadingStage}
              </p>
              {loadingCount > 0 && <p className="text-3xl font-bold text-blue-600" data-id="element-521">
                  {loadingCount}
                </p>}
              <p className="text-sm text-gray-500" data-id="element-522">
                
                While you wait, keep an eye on the news ticker at the bottom of the screen.              </p>
            </div>
          </div>}

        {/* Feed */}
        {!loading && <div className="space-y-4" data-id="element-523">
            {filteredOpportunities.length === 0 ? <div className="bg-white rounded-lg border border-gray-200 p-12 text-center" data-id="element-524">
                <p className="text-gray-600" data-id="element-525">
                  No opportunities found in this category.
                </p>
                <button onClick={loadOpportunities} className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors" data-id="element-526">
                  Try Refreshing
                </button>
              </div> : filteredOpportunities.map((opp, index) => <div key={index} className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-527">
                  <div className="p-5" data-id="element-528">
                    <div className="flex items-start gap-4 mb-3" data-id="element-529">
                      {/* Upvote */}
                      <div className="flex flex-col items-center gap-1 pt-1" data-id="element-530">
                        <button className="text-gray-400 hover:text-orange-500 transition-colors" data-id="element-531">
                          <ArrowUpIcon size={20} data-id="element-532" />
                        </button>
                        <span className="text-sm font-semibold text-gray-700" data-id="element-533">
                          {opp.upvotes}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0" data-id="element-534">
                        {/* Badges */}
                        <div className="flex items-center gap-2 mb-2 flex-wrap" data-id="element-535">
                          <span className={`px-2 py-1 rounded text-xs font-semibold flex items-center gap-1 ${getCategoryStyle(opp.category)}`} data-id="element-536">
                            {getCategoryIcon(opp.category)}
                            {opp.category.toUpperCase()}
                          </span>
                          {getPriorityBadge(opp.priority)}
                          <span className="text-xs text-gray-500" data-id="element-537">
                            {formatTimeAgo(opp.pubDate)}
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 leading-snug" data-id="element-538">
                          {opp.title}
                        </h3>

                        {/* Description */}
                        {opp.description && <p className="text-gray-600 mb-3 leading-relaxed text-sm" data-id="element-539">
                            {opp.description}
                          </p>}

                        {/* Actions */}
                        <div className="flex items-center gap-3" data-id="element-540">
                          <a href={opp.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors" data-id="element-541">
                            View on Reddit
                            <ExternalLinkIcon size={14} data-id="element-542" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>)}
          </div>}

        {/* Refresh */}
        {!loading && opportunities.length > 0 && <div className="text-center mt-6" data-id="element-543">
            <button onClick={loadOpportunities} className="inline-flex items-center gap-2 px-6 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-544">
              <RefreshCwIcon size={16} data-id="element-545" />
              Refresh Feed
            </button>
          </div>}
      </div>
    </div>;
}